/* Falling autumn leaves canvas overlay — mirrors PetalOverlay for reference (sakura). */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import leafSvg from '../../assets/events/autumn-leaves/gold/decorations/leaf.svg'
import {
  OVERLAY_MAX_DPR,
  OVERLAY_TARGET_FPS,
  bucketScale,
  createAdaptiveQuality,
  createFrameGate,
  createSpriteCache,
} from './overlayPerf'

// Kill-switch for the falling-leaf animation. Perf is guarded by overlayPerf
// (DPR cap, 30fps throttle, adaptive quality that stops the overlay on
// low-spec devices); flip to false to hard-disable while keeping the static
// autumn theme CSS.
const LEAF_ANIMATION_ENABLED: boolean = true

type Leaf = {
  x: number
  y: number
  scale: number
  speedY: number
  speedX: number
  baseSpeedX: number
  sway: number
  swaySpeed: number
  swayOffset: number
  rotation: number
  rotationSpeed: number
  tumbleActive: boolean
  tumbleProgress: number
  tumbleDirection: number
  spiralPath: boolean
  spiralAmplitude: number
  spiralPhase: number
  alpha: number
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n))

const createLeaf = (canvasWidth: number, canvasHeight: number): Leaf => {
  const scale = 0.5 + Math.random() * 0.9 // 0.5 to 1.4 — leaves a bit larger than petals
  const baseSpeed = 0.45 + Math.random() * 0.7 // slightly slower fall than petals
  const spiralPath = Math.random() < 0.3 // 30% follow spiral paths

  return {
    x: Math.random() * canvasWidth,
    y: -20 - Math.random() * canvasHeight * 0.3,
    scale,
    speedY: baseSpeed * (0.4 + scale * 0.9), // larger leaves fall faster
    speedX: -0.08 + Math.random() * 0.16,
    baseSpeedX: -0.08 + Math.random() * 0.16,
    sway: 3 + Math.random() * 5, // gentle side drift
    swaySpeed: 0.003 + Math.random() * 0.005,
    swayOffset: Math.random() * Math.PI * 2,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (-0.02 + Math.random() * 0.04) * (1 / scale),
    tumbleActive: false,
    tumbleProgress: 0,
    tumbleDirection: Math.random() < 0.5 ? 1 : -1,
    spiralPath,
    spiralAmplitude: spiralPath ? 6 + Math.random() * 10 : 0,
    spiralPhase: Math.random() * Math.PI * 2,
    alpha: 0.75 + scale * 0.25,
  }
}

const drawLeaf = (
  ctx: CanvasRenderingContext2D,
  leaf: Leaf,
  sprite: HTMLCanvasElement,
  dpr: number,
) => {
  const flip = leaf.tumbleActive ? Math.cos(leaf.tumbleProgress * Math.PI) : 1
  const fx = leaf.tumbleDirection === 1 ? flip : 1
  const fy = leaf.tumbleDirection === -1 ? flip : 1
  const cos = Math.cos(leaf.rotation)
  const sin = Math.sin(leaf.rotation)

  ctx.globalAlpha = leaf.alpha
  ctx.setTransform(
    dpr * fx * cos,
    dpr * fx * sin,
    -dpr * fy * sin,
    dpr * fy * cos,
    dpr * leaf.x,
    dpr * leaf.y,
  )
  // Blit the pre-rasterized sprite (source viewBox 19x18; bucketed ~20px base)
  ctx.drawImage(sprite, -sprite.width / 2, -sprite.height / 2)
}

const LeafOverlay = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const leavesRef = useRef<Leaf[]>([])
  const tickRef = useRef<number>(0)

  const [leafImage, setLeafImage] = useState<HTMLImageElement | null>(null)
  const isVisibleRef = useRef(true)
  const mouseRef = useRef({ x: -1000, y: -1000 })

  const event = useCurrentEvent()

  const shouldRender = useMemo(() => {
    if (!LEAF_ANIMATION_ENABLED) return false
    if (event !== EventType.AUTUMN_LEAVES) return false
    if (typeof window === 'undefined') return false
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches
    return !reduceMotion
  }, [event])

  // Load SVG as image (only while the overlay may render)
  useEffect(() => {
    if (!shouldRender) return
    const img = new Image()
    img.onload = () => setLeafImage(img)
    img.src = leafSvg
  }, [shouldRender])

  // Page visibility detection
  useEffect(() => {
    if (!shouldRender) return
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [shouldRender])

  // Mouse tracking
  useEffect(() => {
    if (!shouldRender) return
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }
    const handleMouseLeave = () => {
      mouseRef.current.x = -1000
      mouseRef.current.y = -1000
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [shouldRender])

  useEffect(() => {
    if (!shouldRender) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    if (!leafImage) return

    const spriteFor = createSpriteCache(leafImage)
    const dpr = clamp(window.devicePixelRatio || 1, 1, OVERLAY_MAX_DPR)
    let quality = createAdaptiveQuality(0)

    const resize = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const isMobile = w < 640
      const leafCount = isMobile
        ? clamp(Math.floor((w * h) / 140000), 6, 14)
        : clamp(Math.floor((w * h) / 75000), 15, 38)
      leavesRef.current = Array.from({ length: leafCount }, () =>
        createLeaf(w, h),
      )
      quality = createAdaptiveQuality(leafCount)
    }

    resize()
    canvas.style.display = ''
    let resizeTimer: number | undefined
    const onResize = () => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(resize, 150)
    }
    window.addEventListener('resize', onResize)

    const gate = createFrameGate(1000 / OVERLAY_TARGET_FPS)
    let last = performance.now()

    const animate = (now: number) => {
      rafRef.current = requestAnimationFrame(animate)

      if (!isVisibleRef.current) {
        last = now
        return
      }

      const interval = now - last
      if (!gate(now)) return

      const canvasW = window.innerWidth
      const canvasH = window.innerHeight
      const dt = clamp(interval / 16.67, 0.5, 3)
      last = now
      tickRef.current += dt
      const workStart = performance.now()

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, canvasW, canvasH)

      const particles = leavesRef.current
      for (let i = 0; i < particles.length; i++) {
        const leaf = particles[i]
        // Mouse repulsion
        const dx = leaf.x - mouseRef.current.x
        const dy = leaf.y - mouseRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const repelRadius = 80

        if (distance < repelRadius && distance > 0) {
          const force = (1 - distance / repelRadius) * 3
          leaf.x += (dx / distance) * force * dt
          leaf.y += (dy / distance) * force * dt
        }

        leaf.y += leaf.speedY * dt

        if (leaf.spiralPath) {
          leaf.spiralPhase += 0.02 * dt
          const spiralOffset =
            Math.sin(leaf.spiralPhase) *
            leaf.spiralAmplitude *
            (leaf.y / canvasH)
          leaf.x += (leaf.speedX + spiralOffset * 0.1) * dt
        } else {
          leaf.x +=
            (leaf.speedX +
              Math.sin(tickRef.current * leaf.swaySpeed + leaf.swayOffset) *
                leaf.sway *
                0.01) *
            dt
        }

        leaf.rotation += leaf.rotationSpeed * dt

        if (leaf.tumbleActive) {
          leaf.tumbleProgress += dt * 0.05
          if (leaf.tumbleProgress >= 1) {
            leaf.tumbleActive = false
            leaf.tumbleProgress = 0
          }
        } else if (leaf.speedY > 1.0 && Math.random() < 0.005 * dt) {
          leaf.tumbleActive = true
          leaf.tumbleProgress = 0
        }

        if (leaf.y > canvasH + 20) {
          Object.assign(leaf, createLeaf(canvasW, canvasH))
          leaf.y = -20
        }
        if (leaf.x < -20) leaf.x = canvasW + 20
        if (leaf.x > canvasW + 20) leaf.x = -20

        leaf.speedX = leaf.speedX * 0.98 + leaf.baseSpeedX * 0.02

        const size = 20 * bucketScale(leaf.scale)
        drawLeaf(ctx, leaf, spriteFor(size, size), dpr)
      }

      ctx.globalAlpha = 1
      quality.record(performance.now() - workStart, interval)

      if (quality.stopped) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        canvas.style.display = 'none'
        return
      }

      const target = quality.count
      while (particles.length > target) particles.pop()
      while (particles.length < target)
        particles.push(createLeaf(canvasW, canvasH))
    }

    rafRef.current = requestAnimationFrame((now) => {
      last = now
      animate(now)
    })

    return () => {
      window.clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [shouldRender, leafImage])

  if (!shouldRender) return null

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 9999 }}
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  )
}

export default LeafOverlay
